import React, { useState, useEffect } from 'react';
import { Calendar, MapPin, Users, Building, ArrowRight, CheckCircle2, Clock, CalendarDays, Map, Sparkles, ExternalLink } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import JobFairFloorMap from '../../components/Recruiter/JobFairFloorMap';
import StudentPrepKit from '../../components/Recruiter/StudentPrepKit';

const StudentJobFairs = () => {
  const { user } = useAuth();
  const [jobFairs, setJobFairs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(null);
  // which fair is expanded for prep kit / floor map
  const [activeFair, setActiveFair] = useState(null);
  const [activeSubTab, setActiveSubTab] = useState('prepkit'); // 'prepkit' | 'floormap'
  const isDark = document.documentElement.classList.contains('dark');

  useEffect(() => {
    fetchJobFairs();
  }, []);

  const fetchJobFairs = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/placements/job-fairs');
      if (data.success) {
        setJobFairs(data.data);
      }
    } catch (error) {
      toast.error('Failed to load Job Fairs');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (fairId) => {
    try {
      setRegistering(fairId);
      const { data } = await api.post(`/placements/job-fairs/${fairId}/register`);
      if (data.success) {
        toast.success('Successfully registered for Job Fair!');
        fetchJobFairs();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to register');
    } finally {
      setRegistering(null);
    }
  };

  const openFairTools = (fair, tab = 'prepkit') => {
    setActiveFair(fair);
    setActiveSubTab(tab);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="relative w-14 h-14">
          <div className="absolute inset-0 rounded-full border-4 border-[#1a3884]/20" />
          <div className="absolute inset-0 rounded-full border-4 border-t-[#1a3884] animate-spin" />
        </div>
      </div>
    );
  }

  // ── Detail panel (expanded view for prep kit / floor map) ──────────────────
  if (activeFair) {
    const isRegistered = activeFair.registeredStudents?.includes(user?._id) || false;
    return (
      <div className="p-6 md:p-8 space-y-6 animate-fade-in">
        {/* back button */}
        <button
          onClick={() => setActiveFair(null)}
          className="flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-[#1a3884] dark:hover:text-[#4f7dff] transition-colors"
        >
          <ArrowRight size={14} className="rotate-180" />
          Back to Job Fairs
        </button>

        {/* Fair header */}
        <div className="relative overflow-hidden rounded-2xl p-5 text-white"
          style={{ background: 'linear-gradient(135deg, #0d1f4e 0%, #1a3884 60%, #2a52b0 100%)' }}>
          <div className="absolute inset-0 opacity-10"
            style={{ backgroundImage: 'radial-gradient(circle at 10% 20%, white 1px, transparent 1px)', backgroundSize: '28px 28px' }} />
          <div className="relative">
            <p className="text-[11px] font-black uppercase tracking-widest text-blue-300 mb-1">{activeFair.label === 'smaart job fair' ? 'SMAART Network Fair' : 'College Fair'}</p>
            <h2 className="text-xl font-black leading-tight mb-2">{activeFair.title}</h2>
            <div className="flex flex-wrap gap-4 text-sm text-blue-200">
              <span className="flex items-center gap-1.5">
                <Calendar size={13} />
                {new Date(activeFair.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
              <span className="flex items-center gap-1.5">
                <MapPin size={13} />
                {activeFair.location || 'TBA'}
              </span>
              <span className="flex items-center gap-1.5">
                <Building size={13} />
                {activeFair.companies?.length || 0} Companies
              </span>
            </div>
          </div>
        </div>

        {/* Sub-tab bar */}
        <div className="flex items-center gap-1 p-1 rounded-2xl" style={{ background: 'rgba(26,56,132,0.06)' }}>
          <button
            onClick={() => setActiveSubTab('prepkit')}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all duration-200 ${activeSubTab === 'prepkit'
              ? 'bg-[#1a3884] text-white shadow-lg shadow-[#1a3884]/30'
              : 'text-slate-500 dark:text-slate-400 hover:text-[#1a3884] dark:hover:text-[#4f7dff]'
              }`}
          >
            <Sparkles size={14} />
            My Prep Kit
          </button>
          <button
            onClick={() => setActiveSubTab('floormap')}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all duration-200 ${activeSubTab === 'floormap'
              ? 'bg-[#1a3884] text-white shadow-lg shadow-[#1a3884]/30'
              : 'text-slate-500 dark:text-slate-400 hover:text-[#1a3884] dark:hover:text-[#4f7dff]'
              }`}
          >
            <Map size={14} />
            Floor Map
          </button>
        </div>

        {/* Sub-tab content */}
        <div className="bg-white dark:bg-[#002147] rounded-2xl border border-gray-100 dark:border-[#1a3884]/25 shadow-sm p-5">
          {activeSubTab === 'prepkit' && (
            <StudentPrepKit
              fairId={activeFair._id}
              student={user}
              isDark={isDark}
            />
          )}
          {activeSubTab === 'floormap' && (
            <JobFairFloorMap
              fairId={activeFair._id}
              isDark={isDark}
              myEntityId={null}
              userRole="student"
            />
          )}
        </div>
      </div>
    );
  }

  // ── Main list ──────────────────────────────────────────────────────────────
  return (
    <div className="p-6 md:p-8 space-y-8 animate-fade-in">
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
          Upcoming Job Fairs
        </h1>
        <p className="text-gray-500 dark:text-gray-400">
          Discover and register for hiring events tailored to your skills.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {jobFairs.length === 0 ? (
          <div className="col-span-full bg-white dark:bg-[#002147] rounded-2xl p-16 text-center border border-gray-100 dark:border-[#1a3884]/30 shadow-sm">
            <div className="w-16 h-16 bg-indigo-50 dark:bg-[#1a3884]/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CalendarDays className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">No Upcoming Fairs</h3>
            <p className="text-gray-500 dark:text-gray-400 max-w-sm mx-auto">
              There are currently no job fairs scheduled. Check back later for new opportunities!
            </p>
          </div>
        ) : (
          jobFairs.map((fair) => {
            const isRegistered = fair.registeredStudents?.includes(user?._id) || false;
            const isSmaartFair = fair.label?.toLowerCase() === 'smaart job fair';

            return (
              <div key={fair._id} className="bg-white dark:bg-[#00152E] rounded-[1.5rem] p-6 border border-gray-100 dark:border-[#1a3884]/20 shadow-lg shadow-[#1a3884]/5 hover:shadow-xl hover:shadow-[#1a3884]/10 transition-all flex flex-col h-full group">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex gap-2">
                    {isSmaartFair ? (
                      <span className="px-3 py-1 bg-gradient-to-r from-indigo-600 to-blue-600 text-white text-[10px] font-bold uppercase tracking-wider rounded-full shadow-sm">
                        Network Fair
                      </span>
                    ) : (
                      <span className="px-3 py-1 bg-gray-100 dark:bg-[#1a3884]/20 text-gray-600 dark:text-indigo-300 text-[10px] font-bold uppercase tracking-wider rounded-full">
                        College Fair
                      </span>
                    )}
                  </div>
                  <div className={"px-2.5 py-1 text-xs font-bold rounded-full " + (fair.status === 'active' ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400')}>
                    {fair.status?.toUpperCase() || 'UPCOMING'}
                  </div>
                </div>

                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 line-clamp-2 group-hover:text-[#1a3884] dark:group-hover:text-indigo-400 transition-colors">
                  {fair.title}
                </h3>

                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 line-clamp-2">
                  {fair.description || 'No description provided.'}
                </p>

                <div className="space-y-3 mb-6 flex-grow">
                  <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-300">
                    <div className="w-8 h-8 rounded-full bg-indigo-50 dark:bg-[#1a3884]/20 flex items-center justify-center shrink-0">
                      <Calendar className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <span>
                      {new Date(fair.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      {fair.endDate && ' - ' + new Date(fair.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  </div>

                  <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-300">
                    <div className="w-8 h-8 rounded-full bg-indigo-50 dark:bg-[#1a3884]/20 flex items-center justify-center shrink-0">
                      <MapPin className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <span className="line-clamp-1">{fair.location || 'Virtual / TBA'}</span>
                  </div>

                  <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-300">
                    <div className="w-8 h-8 rounded-full bg-indigo-50 dark:bg-[#1a3884]/20 flex items-center justify-center shrink-0">
                      <Building className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <span>{fair.companies?.length || 0} Companies Participating</span>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-100 dark:border-[#1a3884]/20 mt-auto space-y-2">
                  {/* Main register / registered button */}
                  {isRegistered ? (
                    <button
                      disabled
                      className="w-full py-3 flex items-center justify-center gap-2 bg-indigo-50 dark:bg-[#1a3884]/30 text-indigo-700 dark:text-indigo-300 font-bold rounded-xl"
                    >
                      <CheckCircle2 className="w-5 h-5" />
                      Registered
                    </button>
                  ) : (
                    <button
                      onClick={() => handleRegister(fair._id)}
                      disabled={registering === fair._id || fair.status !== 'active'}
                      className={"w-full py-3 flex items-center justify-center gap-2 font-bold rounded-xl transition-all shadow-md transform active:scale-95 " + (fair.status !== 'active' ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 cursor-not-allowed shadow-none' : 'bg-[#1a3884] hover:bg-[#00152E] text-white shadow-[#1a3884]/20 hover:shadow-lg hover:shadow-[#1a3884]/30 hover:-translate-y-0.5')}
                    >
                      {registering === fair._id ? (
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <>
                          Register Now
                          <ArrowRight className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  )}

                  {/* Prep Kit + Floor Map buttons — show for all, especially useful after registration */}
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => openFairTools(fair, 'prepkit')}
                      className="flex items-center justify-center gap-1.5 py-2 text-[11px] font-bold rounded-xl border transition-all hover:bg-[#1a3884]/5 dark:hover:bg-[#1a3884]/15"
                      style={{ borderColor: 'rgba(26,56,132,0.25)', color: '#1a3884' }}
                    >
                      <Sparkles size={11} />
                      Prep Kit
                    </button>
                    <button
                      onClick={() => openFairTools(fair, 'floormap')}
                      className="flex items-center justify-center gap-1.5 py-2 text-[11px] font-bold rounded-xl border transition-all hover:bg-[#1a3884]/5 dark:hover:bg-[#1a3884]/15"
                      style={{ borderColor: 'rgba(26,56,132,0.25)', color: '#1a3884' }}
                    >
                      <Map size={11} />
                      Floor Map
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default StudentJobFairs;
